import React, { useState } from 'react';

import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';

import {
  type LongQueueFields,
  type LongestQueueFields,
  type NoVisitorsFields,
  deriveLongQueueColor,
  deriveNoVisitorsColor,
  derivePeakQueueColor,
  ruHours,
  ruMinutes,
  ruPeople,
  serializeLongQueue,
  serializeLongestQueue,
  serializeNoVisitors,
} from './treeBlock';

/**
 * In-place editing for the three "Очередь и бар" rows (Alexey 2026-09-04
 * spec, in his words: "no-visitors line in-place editable … long-queue line
 * in-place with a 'не наблюдалась' checkbox replacing the value …
 * auto-color/severity shown as the LAST value per field group, computed
 * live but overridable, rendered as a clickable square/dropdown for inline
 * rows"). Every change writes straight through (`onChange(row)`) — there is
 * no Apply button; the row IS the form. Colour: always computed from the
 * values (mirrors checkout_control.py 1:1, see treeBlock.ts's derive*) —
 * the original "overridable" half of that spec was dropped on 2026-09-08
 * (Alexey: «кружки всегда автоматом, разметчики делают только параметры»),
 * so the square is display-only and the serialisers ignore any stored
 * colour anyway.
 */

function NumInput({
  value,
  onChange,
  min = 0,
  disabled,
  width = 'w-14',
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  disabled?: boolean;
  width?: string;
}) {
  return (
    <input
      type="number"
      min={min}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(Math.max(min, Number(e.target.value) || 0))}
      className={cn(
        'mx-0.5 inline-block h-6 rounded-md border border-input bg-background px-1.5 text-center text-sm tabular-nums',
        'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:opacity-50',
        width,
      )}
    />
  );
}

/** The colour square — display only (see the module comment). */
export function ColorSquare({ value }: { value: string }) {
  return <span className="inline-flex h-6 items-center px-0.5 align-middle text-base leading-none">{value}</span>;
}

// ── "Очередь от N человек: …" ──────────────────────────────────────────────

export function InlineLongQueueRow({
  initial,
  maxCashiers,
  disabled,
  onChange,
}: {
  initial: LongQueueFields;
  /** Zone's cashier count — the "открыли вторую кассу" tail only makes
   *  sense with 2+ (Alexey: "second-cashier line conditional on existence"). */
  maxCashiers: number | null;
  disabled?: boolean;
  onChange: (row: string) => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<LongQueueFields>(initial);
  const color = deriveLongQueueColor(f.minutes, f.none);
  const hasSecondCashier = (maxCashiers ?? 0) > 1 || initial.secondCashierOn;

  const update = (patch: Partial<LongQueueFields>) => {
    const next = { ...f, ...patch };
    setF(next);
    onChange(serializeLongQueue(next));
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-1 leading-7">
      <ColorSquare value={color} />
      <span>Очередь от</span>
      <NumInput value={f.threshold} onChange={(n) => update({ threshold: n })} disabled={disabled} />
      <span>человек</span>
      <label className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={f.none}
          disabled={disabled}
          onChange={(e) => update({ none: e.target.checked })}
          className="size-3.5"
        />
        {t('reportValidation.tree.queueNoneInline')}
      </label>
      {!f.none && (
        <>
          <span>:</span>
          <NumInput value={f.minutes} onChange={(n) => update({ minutes: n })} disabled={disabled} />
          <span className={cn(color !== '🟢' && 'underline')}>{ruMinutes(Math.round(f.minutes)).replace(/^\d+\s/, '')}</span>
          {hasSecondCashier && (
            <>
              <span>└</span>
              <label className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={f.secondCashierOn}
                  disabled={disabled}
                  onChange={(e) => update({ secondCashierOn: e.target.checked })}
                  className="size-3.5"
                />
                <span className={cn(!f.secondCashierOn && 'text-muted-foreground')}>
                  {t('reportValidation.tree.secondCashierInline')}:
                </span>
              </label>
              {f.secondCashierOn && (
                <>
                  <NumInput
                    value={f.secondCashierMinutes}
                    onChange={(n) => update({ secondCashierMinutes: n })}
                    disabled={disabled}
                  />
                  <span>{ruMinutes(Math.round(f.secondCashierMinutes)).replace(/^\d+\s/, '')}</span>
                </>
              )}
            </>
          )}
        </>
      )}
    </span>
  );
}

// ── "Без посетителей у кассы: N часов из M" ────────────────────────────────

export function InlineNoVisitorsRow({
  initial,
  disabled,
  onChange,
}: {
  initial: NoVisitorsFields;
  disabled?: boolean;
  onChange: (row: string) => void;
}) {
  const [f, setF] = useState<NoVisitorsFields>(initial);
  const color = deriveNoVisitorsColor(f.zeroHours, f.totalHours);

  const update = (patch: Partial<NoVisitorsFields>) => {
    const next = { ...f, ...patch };
    setF(next);
    onChange(serializeNoVisitors(next));
  };

  // Total hours = the branch's open→close span, computed by the backend;
  // only the zero-hours count is the validator's to correct (Alexey:
  // "total hours auto from open/close, only zero-hours editable").
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1 leading-7">
      <ColorSquare value={color} />
      <span>Без посетителей у кассы:</span>
      <NumInput value={f.zeroHours} onChange={(n) => update({ zeroHours: n })} disabled={disabled} />
      <span className={cn(color !== '🟢' && 'underline')}>
        {ruHours(Math.round(f.zeroHours)).replace(/^\d+\s/, '')} из {f.totalHours}
      </span>
    </span>
  );
}

// ── "Самая длинная очередь" block ──────────────────────────────────────────

export function InlineLongestQueueRow({
  initial,
  disabled,
  onChange,
}: {
  initial: LongestQueueFields;
  disabled?: boolean;
  onChange: (rows: string[]) => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<LongestQueueFields>(initial);
  const color = derivePeakQueueColor(f.peopleCount);

  const update = (patch: Partial<LongestQueueFields>) => {
    const next = { ...f, ...patch };
    setF(next);
    onChange(serializeLongestQueue(next));
  };
  const unmeasured = f.drainMinutes == null;

  return (
    <span className="flex flex-col leading-7">
      <span className="inline-flex items-center gap-x-1">
        <ColorSquare value={color} />
        <span>Самая длинная очередь</span>
      </span>
      <span className="inline-flex items-center gap-x-1 pl-4">
        <span className="text-muted-foreground">├</span>
        <NumInput value={f.peopleCount} onChange={(n) => update({ peopleCount: n })} disabled={disabled} />
        <span className={cn(color !== '🟢' && 'underline')}>{ruPeople(f.peopleCount).replace(/^\d+\s/, '')}</span>
      </span>
      <span className="inline-flex flex-wrap items-center gap-x-1 pl-4">
        <span className="text-muted-foreground">└</span>
        {!unmeasured && (
          <>
            <span>Спала за</span>
            <NumInput value={f.drainMinutes ?? 1} min={1} onChange={(n) => update({ drainMinutes: n })} disabled={disabled} />
            <span>мин</span>
          </>
        )}
        <label className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={unmeasured}
            disabled={disabled}
            onChange={(e) => update({ drainMinutes: e.target.checked ? null : Math.max(1, initial.drainMinutes ?? 1) })}
            className="size-3.5"
          />
          {t('reportValidation.tree.drainUnmeasured')}
        </label>
      </span>
    </span>
  );
}
